export const SKIP_ONBOARDING_CREATE_LOCK_NAME = 'skip-onboarding-create';

export type WithSkipOnboardingCreateLockResult<T> = { acquired: true; result: T } | { acquired: false };

/**
 * Runs `fn` while holding a cross-tab Web Lock so only one tab creates a workspace
 * during skip-onboarding. The lock is released when `fn` finishes or the tab closes.
 *
 * Callers must keep `fn` to the create API only — holding the lock across post-create
 * navigation can leave it stuck if `router.replace` stalls (e.g. in Cypress), blocking
 * the next tab/test from creating.
 *
 * When Web Locks is unavailable (e.g. non-secure contexts), runs `fn` without a lock
 * so skip-onboarding can still create a workspace in this tab.
 */
export const withSkipOnboardingCreateLock = async <T>(
  fn: () => Promise<T>,
): Promise<WithSkipOnboardingCreateLockResult<T>> => {
  if (typeof navigator === 'undefined' || !navigator.locks?.request) {
    const result = await fn();

    return { acquired: true, result };
  }

  let outcome: WithSkipOnboardingCreateLockResult<T> = { acquired: false };

  await navigator.locks.request(SKIP_ONBOARDING_CREATE_LOCK_NAME, { ifAvailable: true }, async (lock) => {
    if (!lock) {
      return;
    }

    outcome = { acquired: true, result: await fn() };
  });

  return outcome;
};
